import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../contexts/UserContext';
import {
  Box,
  Typography,
  Paper,
  Alert,
  TextField,
  IconButton,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Grid,
  Tooltip,
  Snackbar,
  CircularProgress,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel, GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { Block, Search, Clear, Download as DownloadIcon, ListAlt, Edit, Visibility, Reply, Feedback, Add as AddIcon, Unsubscribe } from '@mui/icons-material';
import contactService, { Contact, Segment } from '../services/contactService';
import { getSegmentColor } from '../utils/segmentColors';
import LoadingState from '../components/LoadingState';
import PageContainer from '../components/PageContainer';
import { useApiService, apiService } from '../services/apiService';
import { AxiosResponse } from 'axios';
import DateRangePicker from '../components/analytics/DateRangePicker';
import { ResponsiveLine } from '@nivo/line';
import { useNavigate, useLocation } from 'react-router-dom';
import { DateRange as DateRangeType } from '../services/analyticsService';
import { format, parseISO } from 'date-fns';
import MuiAlert from '@mui/material/Alert';
import ContactModal from '../components/ContactModal';
import ContactDetailModal from '../components/ContactDetailModal';
import { obfuscateEmail } from '../utils/roleUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contacts-tabpanel-${index}`}
      aria-labelledby={`contacts-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `contacts-tab-${index}`,
    'aria-controls': `contacts-tabpanel-${index}`,
  };
}

const Contacts: React.FC = () => {
  const auth = useUser();
  const { selectedClientId, isICRole } = useUser();
  // Initialize API service with auth context
  useApiService();
  
  // Add refs for tracking active requests
  const contactsAbortController = useRef<AbortController | null>(null);
  const segmentsAbortController = useRef<AbortController | null>(null);
  const requestTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const segmentsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorTimeout, setErrorTimeout] = useState<NodeJS.Timeout | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchError, setSearchError] = useState<string>("");
  const [segments, setSegments] = useState<Segment[]>([]);
  const [segmentsLoading, setSegmentsLoading] = useState(true);
  const [selectedSegment, setSelectedSegment] = useState<number | ''>('');
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    quickFilterValues: [],
  });
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<number>(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'email_address', sort: 'asc' }]);
  const location = useLocation();
  const navigate = useNavigate();

  const tabMap = { list: 0, bounces: 1, complaints: 2, unsubscribes: 3 } as const;
  const reverseTabMap: Record<number, string> = { 0: 'list', 1: 'bounces', 2: 'complaints', 3: 'unsubscribes' };

  const [tabValue, setTabValue] = useState<number>(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && tabParam in tabMap) {
      return tabMap[tabParam as keyof typeof tabMap];
    }
    return 0; // Default to list
  });

  const [bounces, setBounces] = useState<any[]>([]);
  const [bouncesByDay, setBouncesByDay] = useState<any[]>([]);
  const [bouncesLoading, setBouncesLoading] = useState(false);
  const [bouncesError, setBouncesError] = useState<string | null>(null);
  const [bouncesDateRange, setBouncesDateRange] = useState<DateRangeType>({ date_range: 'last_30_days' });

  const [complaints, setComplaints] = useState<any[]>([]);
  const [complaintsByDay, setComplaintsByDay] = useState<any[]>([]);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [complaintsError, setComplaintsError] = useState<string | null>(null);
  const [complaintsDateRange, setComplaintsDateRange] = useState<DateRangeType>({ date_range: 'last_30_days' });

  const [unsubscribes, setUnsubscribes] = useState<any[]>([]);
  const [unsubscribesByDay, setUnsubscribesByDay] = useState<any[]>([]);
  const [unsubscribesLoading, setUnsubscribesLoading] = useState(false);
  const [unsubscribesError, setUnsubscribesError] = useState<string | null>(null);
  const [unsubscribesDateRange, setUnsubscribesDateRange] = useState<DateRangeType>({ date_range: 'last_30_days' });

  const [selectedBounceDate, setSelectedBounceDate] = useState<string | null>(null);
  const [selectedComplaintDate, setSelectedComplaintDate] = useState<string | null>(null);
  const [selectedUnsubscribeDate, setSelectedUnsubscribeDate] = useState<string | null>(null);

  // Pagination state for bounces, complaints, and unsubscribes
  const [bouncesPaginationModel, setBouncesPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [bouncesTotalCount, setBouncesTotalCount] = useState(0);
  const [bouncesSortModel, setBouncesSortModel] = useState<GridSortModel>([]);
  const [complaintsPaginationModel, setComplaintsPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [complaintsTotalCount, setComplaintsTotalCount] = useState(0);
  const [complaintsSortModel, setComplaintsSortModel] = useState<GridSortModel>([]);
  const [unsubscribesPaginationModel, setUnsubscribesPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [unsubscribesTotalCount, setUnsubscribesTotalCount] = useState(0);
  const [unsubscribesSortModel, setUnsubscribesSortModel] = useState<GridSortModel>([]);

  // Filter state for bounces, complaints, and unsubscribes
  const [bouncesFilterModel, setBouncesFilterModel] = useState<GridFilterModel>({ items: [] });
  const [complaintsFilterModel, setComplaintsFilterModel] = useState<GridFilterModel>({ items: [] });
  const [unsubscribesFilterModel, setUnsubscribesFilterModel] = useState<GridFilterModel>({ items: [] });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Add Contact Modal state
  const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);
  const [selectedContactForDetail, setSelectedContactForDetail] = useState<Contact | null>(null);
  const [isContactDetailModalOpen, setIsContactDetailModalOpen] = useState(false);
  const [openInEditMode, setOpenInEditMode] = useState(false);

  // Cleanup function to cancel pending requests and timeouts
  const cancelPendingRequests = () => {
    if (contactsAbortController.current) {
      contactsAbortController.current.abort();
      contactsAbortController.current = null;
    }
    if (segmentsAbortController.current) {
      segmentsAbortController.current.abort();
      segmentsAbortController.current = null;
    }
    if (requestTimeoutRef.current) {
      clearTimeout(requestTimeoutRef.current);
      requestTimeoutRef.current = null;
    }
    if (errorTimeout) {
      clearTimeout(errorTimeout);
      setErrorTimeout(null);
    }
  };

  const setErrorWithDelay = (message: string) => {
    // Clear any existing error timeout
    if (errorTimeout) {
      clearTimeout(errorTimeout);
    }
    // Set error after a delay to prevent flashing
    const timeout = setTimeout(() => {
      setError(message);
    }, 1000);
    setErrorTimeout(timeout);
  };

  // Effect cleanup
  useEffect(() => {
    return () => {
      cancelPendingRequests();
    };
  }, []);

  const fetchContacts = async (page: number, pageSize: number, sortField?: string, sortOrder?: string) => {
    if (!selectedClientId || !auth.isAuthenticated || auth.isLoading) {
      console.log('Skipping contacts fetch:', { 
        hasClientId: !!selectedClientId, 
        isAuthenticated: auth.isAuthenticated,
        isLoading: auth.isLoading 
      });
      return;
    }

    // Cancel any pending contacts request
    if (contactsAbortController.current) {
      contactsAbortController.current.abort();
      contactsAbortController.current = null;
    }
    if (errorTimeout) {
      clearTimeout(errorTimeout);
      setErrorTimeout(null);
    }
    contactsAbortController.current = new AbortController();

    try {
      setLoading(true);
      setError(null);
      setSearchError("");
  
      const params: any = {
        skip: page * pageSize,
        limit: pageSize,
        client_id: selectedClientId,
        sort_by_field: sortField || 'email_address',
        sort_order: sortOrder || 'asc'
      };
      
      console.log('Fetching contacts with params:', params);

      if (searchTerm?.trim()) {
        if (searchTerm.trim().length < 3) {
          setSearchError("Search term must be at least 3 characters");
          setLoading(false);
          return;
        }
        // Use unified search term that searches across all fields
        params.search_term = searchTerm.trim();
        console.log('Using unified search term:', params.search_term);
      }
      
      if (selectedSegment) {
        params.segment_filter = selectedSegment;
      }
      
      console.log('Fetching contacts with params:', params);
      
      const contactsData = await contactService.getContacts(params, {
        signal: contactsAbortController.current.signal
      });

      // Only update state if the component is still mounted and this is the latest request
      if (contactsAbortController.current) {
        if (!contactsData) {
          throw new Error('No contacts data received');
        }
        if (!Array.isArray(contactsData.contacts)) {
          console.error('Invalid contacts data structure:', contactsData);
          throw new Error('Invalid contacts data structure received');
        }
        
        setContacts(contactsData.contacts);
        setTotalCount(contactsData.total_count);
      }
    } catch (err: any) {
      // Only set error if it's not an aborted request
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        if (err?.response?.status === 400) {
          setSearchError(err.response.data.detail);
        } else {
          setErrorWithDelay("Failed to fetch contacts");
        }
        console.error("Error fetching contacts:", err);
      }
    } finally {
      if (contactsAbortController.current) {
        setLoading(false);
      }
    }
  };

  const fetchSegments = async () => {
    if (!selectedClientId || !auth.isAuthenticated || auth.isLoading) {
      console.log('Skipping segments fetch:', { 
        hasClientId: !!selectedClientId, 
        isAuthenticated: auth.isAuthenticated,
        isLoading: auth.isLoading 
      });
      return;
    }

    // Cancel any pending segments request
    if (segmentsAbortController.current) {
      segmentsAbortController.current.abort();
      segmentsAbortController.current = null;
    }
    segmentsAbortController.current = new AbortController();
    
    try {
      setSegmentsLoading(true);
      const segmentsData = await contactService.getSegments(selectedClientId, {
        signal: segmentsAbortController.current.signal
      });
      
      // Only update state if the component is still mounted and this is the latest request
      if (segmentsAbortController.current) {
        setSegments(segmentsData);
      }
    } catch (err) {
      // Only show error if it's not an abort error and component is mounted
      if (err instanceof Error && 
          err.name !== 'CanceledError' && 
          err.name !== 'AbortError' && 
          segmentsAbortController.current) {
        console.error('Error fetching segments:', err);
      }
    } finally {
      if (segmentsAbortController.current) {
        setSegmentsLoading(false);
      }
    }
  };

  const handleDirectExport = async () => {
    if (!selectedClientId || !auth.isAuthenticated) return;

    try {
      setIsExporting(true);
      setExportProgress(0);

      // Build query parameters
      const params = new URLSearchParams();
      params.append('client_id', selectedClientId.toString());
      if (searchTerm) params.append('email_filter', searchTerm);
      if (selectedSegment) params.append('segment_filter', selectedSegment.toString());

      // Create URL for direct export
      const url = `/api/contacts/direct-export?${params.toString()}`;
      
      const response = await apiService.get<Blob>(url, {
        responseType: 'blob',
        headers: { Accept: 'text/csv' },
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setExportProgress(progress);
          }
        }
      }) as AxiosResponse<Blob>;

      // Get filename from response headers
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename = 'contacts.csv';
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Create blob and trigger download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export failed:', error);
      setError('Failed to export contacts');
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  const handleBouncesExport = async () => {
    if (!selectedClientId || !auth.isAuthenticated) return;

    try {
      setIsExporting(true);
      setExportProgress(0);

      // Build query parameters with date range
      const params = new URLSearchParams();
      params.append('client_id', selectedClientId.toString());
      const { startDate, endDate } = getStartEndFromDateRange(bouncesDateRange);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      // Create URL for bounces export
      const url = `/api/contacts/bounces/export?${params.toString()}`;
      
      const response = await apiService.get<Blob>(url, {
        responseType: 'blob',
        headers: { Accept: 'text/csv' },
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setExportProgress(progress);
          }
        }
      }) as AxiosResponse<Blob>;

      // Get filename from response headers
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename;
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      if (!filename) {
        filename = 'bounces.csv'; // Fallback only if header parsing fails
      }

      // Create blob and trigger download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export failed:', error);
      setError('Failed to export bounces');
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  const handleComplaintsExport = async () => {
    if (!selectedClientId || !auth.isAuthenticated) return;

    try {
      setIsExporting(true);
      setExportProgress(0);

      // Build query parameters with date range
      const params = new URLSearchParams();
      params.append('client_id', selectedClientId.toString());
      const { startDate, endDate } = getStartEndFromDateRange(complaintsDateRange);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      // Create URL for complaints export
      const url = `/api/contacts/complaints/export?${params.toString()}`;
      
      const response = await apiService.get<Blob>(url, {
        responseType: 'blob',
        headers: { Accept: 'text/csv' },
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setExportProgress(progress);
          }
        }
      }) as AxiosResponse<Blob>;

      // Get filename from response headers
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename;
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      if (!filename) {
        filename = 'complaints.csv'; // Fallback only if header parsing fails
      }

      // Create blob and trigger download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export failed:', error);
      setError('Failed to export complaints');
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  const handleUnsubscribesExport = async () => {
    if (!selectedClientId || !auth.isAuthenticated) return;

    try {
      setIsExporting(true);
      setExportProgress(0);

      // Build query parameters with date range
      const params = new URLSearchParams();
      params.append('client_id', selectedClientId.toString());
      const { startDate, endDate } = getStartEndFromDateRange(unsubscribesDateRange);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      // Create URL for unsubscribes export
      const url = `/api/contacts/unsubscribes/export?${params.toString()}`;
      
      const response = await apiService.get<Blob>(url, {
        responseType: 'blob',
        headers: { Accept: 'text/csv' },
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setExportProgress(progress);
          }
        }
      }) as AxiosResponse<Blob>;

      // Get filename from response headers
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename;
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      if (!filename) {
        filename = 'unsubscribes.csv'; // Fallback only if header parsing fails
      }

      // Create blob and trigger download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export failed:', error);
      setError('Failed to export unsubscribes');
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  // Effect for contacts
  useEffect(() => {
    if (auth.isLoading) {
      return;
    }

    // Cancel any existing timeout
    if (requestTimeoutRef.current) {
      clearTimeout(requestTimeoutRef.current);
    }

    // Set new timeout
    requestTimeoutRef.current = setTimeout(() => {
      if (auth.isAuthenticated && selectedClientId) {
        const currentSort = sortModel.length > 0 ? sortModel[0] : { field: 'email_address', sort: 'asc' };
        fetchContacts(0, 100, currentSort.field, currentSort.sort);
      }
    }, 500);

    return () => {
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current);
      }
      cancelPendingRequests();
    };
  }, [auth.isLoading, auth.isAuthenticated, selectedClientId, searchTerm, selectedSegment]);

  // Effect for segments
  useEffect(() => {
    if (auth.isLoading) {
      return;
    }

    // Cancel any existing timeout for segments
    if (segmentsTimeoutRef.current) {
      clearTimeout(segmentsTimeoutRef.current);
    }

    // Set new timeout with a delay to avoid race conditions with contacts fetch
    segmentsTimeoutRef.current = setTimeout(() => {
      if (auth.isAuthenticated && selectedClientId) {
        fetchSegments();
      }
    }, 200); // Delay segments fetch by 200ms after contacts

    return () => {
      if (segmentsTimeoutRef.current) {
        clearTimeout(segmentsTimeoutRef.current);
      }
      cancelPendingRequests();
    };
  }, [auth.isLoading, auth.isAuthenticated, selectedClientId]);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    const currentSort = sortModel.length > 0 ? sortModel[0] : { field: 'email_address', sort: 'asc' };
    fetchContacts(model.page, model.pageSize, currentSort.field, currentSort.sort);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    console.log('Sort model changed:', model);
    setSortModel(model);
    if (model.length > 0) {
      console.log('Fetching contacts with sort:', model[0].field, model[0].sort);
      fetchContacts(0, 100, model[0].field, model[0].sort); // Reset to first page when sorting changes
    }
  };


  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSegmentChange = (event: SelectChangeEvent<number | ''>) => {
    setSelectedSegment(event.target.value as number | '');
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedSegment('');
  };

  // Add Contact Modal handlers
  const handleOpenAddContactModal = () => {
    setIsAddContactModalOpen(true);
  };

  const handleCloseAddContactModal = () => {
    setIsAddContactModalOpen(false);
  };

  const handleOpenContactDetailModal = (contact: Contact, editMode: boolean = false) => {
    setSelectedContactForDetail(contact);
    setOpenInEditMode(editMode);
    setIsContactDetailModalOpen(true);
  };

  const handleCloseContactDetailModal = () => {
    setIsContactDetailModalOpen(false);
    setSelectedContactForDetail(null);
    setOpenInEditMode(false);
  };

  const handleContactCreated = () => {
    // Refresh the contacts list
    const currentSort = sortModel.length > 0 ? sortModel[0] : { field: 'email_address', sort: 'asc' };
    fetchContacts(0, 100, currentSort.field, currentSort.sort);
    setSnackbarMessage('Contact created successfully!');
    setSnackbarOpen(true);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const newTab = reverseTabMap[newValue];
    navigate(`/app/contacts?tab=${newTab}`, { replace: true });
  };

  // Helper to get start/end dates from DateRangeType
  function getStartEndFromDateRange(dateRange: DateRangeType): { startDate?: string; endDate?: string; days?: number } {
    if (dateRange.date_range === 'custom' && dateRange.startDate && dateRange.endDate) {
      return {
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
      };
    } else {
      const end = new Date();
      let start = new Date();
      let days = 30;
      switch (dateRange.date_range) {
        case 'last_7_days':
          start.setDate(end.getDate() - 7);
          days = 7;
          break;
        case 'last_30_days':
          start.setDate(end.getDate() - 30);
          days = 30;
          break;
        case 'last_90_days':
          start.setDate(end.getDate() - 90);
          days = 90;
          break;
        case 'last_12_months':
          start.setMonth(end.getMonth() - 12);
          days = 365;
          break;
        default:
          start.setDate(end.getDate() - 30);
          days = 30;
      }
      return {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        days,
      };
    }
  }

  // Fetch bounces
  const fetchBounces = async () => {
    if (!selectedClientId) return;
    setBouncesLoading(true);
    setBouncesError(null);
    const { startDate, endDate, days } = getStartEndFromDateRange(bouncesDateRange);
    const skip = bouncesPaginationModel.page * bouncesPaginationModel.pageSize;
    const limit = bouncesPaginationModel.pageSize;
    
    // Calculate actual days from date range for by-day API
    let actualDays = days || 30;
    if (bouncesDateRange.date_range === 'custom' && bouncesDateRange.startDate && bouncesDateRange.endDate) {
      const start = new Date(bouncesDateRange.startDate);
      const end = new Date(bouncesDateRange.endDate);
      actualDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    }
    
    // Get sorting parameters
    const sortBy = bouncesSortModel.length > 0 ? mapBouncesFieldName(bouncesSortModel[0].field) : undefined;
    const sortOrder = bouncesSortModel.length > 0 ? (bouncesSortModel[0].sort || undefined) : undefined;
    
    // Get filter parameters
    const filters = mapBouncesFilters(bouncesFilterModel);
    
    Promise.all([
      apiService.getBounces({ 
        clientId: selectedClientId, 
        startDate, 
        endDate, 
        skip, 
        limit, 
        sortBy, 
        sortOrder,
        ...filters
      }) as Promise<{ data: any[], total?: number }>,
      apiService.getBouncesByDay({ clientId: selectedClientId, days: actualDays }) as Promise<{ data: any[] }>
    ])
      .then(([listRes, byDayRes]) => {
        setBounces(listRes.data as any[]);
        setBouncesByDay(byDayRes.data as any[]);
        
        // Calculate total count from by-day data
        const totalFromByDay = byDayRes.data.reduce((sum: number, day: any) => sum + (day.count || 0), 0);
        setBouncesTotalCount(totalFromByDay);
      })
      .catch(err => setBouncesError(err.message || 'Failed to load bounces'))
      .finally(() => setBouncesLoading(false));
  };

  // Fetch complaints
  const fetchComplaints = async () => {
    if (!selectedClientId) return;
    setComplaintsLoading(true);
    setComplaintsError(null);
    const { startDate, endDate, days } = getStartEndFromDateRange(complaintsDateRange);
    const skip = complaintsPaginationModel.page * complaintsPaginationModel.pageSize;
    const limit = complaintsPaginationModel.pageSize;
    
    // Calculate actual days from date range for by-day API
    let actualDays = days || 30;
    if (complaintsDateRange.date_range === 'custom' && complaintsDateRange.startDate && complaintsDateRange.endDate) {
      const start = new Date(complaintsDateRange.startDate);
      const end = new Date(complaintsDateRange.endDate);
      actualDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    }
    
    // Get sorting parameters
    const sortBy = complaintsSortModel.length > 0 ? mapComplaintsFieldName(complaintsSortModel[0].field) : undefined;
    const sortOrder = complaintsSortModel.length > 0 ? (complaintsSortModel[0].sort || undefined) : undefined;
    
    // Get filter parameters
    const filters = mapComplaintsFilters(complaintsFilterModel);
    
    Promise.all([
      apiService.getComplaints({ 
        clientId: selectedClientId, 
        startDate, 
        endDate, 
        skip, 
        limit, 
        sortBy, 
        sortOrder,
        ...filters
      }) as Promise<{ data: any[], total?: number }>,
      apiService.getComplaintsByDay({ clientId: selectedClientId, days: actualDays }) as Promise<{ data: any[] }>
    ])
      .then(([listRes, byDayRes]) => {
        setComplaints(listRes.data as any[]);
        setComplaintsByDay(byDayRes.data as any[]);
        
        // Calculate total count from by-day data
        const totalFromByDay = byDayRes.data.reduce((sum: number, day: any) => sum + (day.count || 0), 0);
        setComplaintsTotalCount(totalFromByDay);
      })
      .catch(err => setComplaintsError(err.message || 'Failed to load complaints'))
      .finally(() => setComplaintsLoading(false));
  };

  useEffect(() => {
    if (tabValue === 1) fetchBounces();
  }, [tabValue, selectedClientId, bouncesDateRange, bouncesPaginationModel, bouncesSortModel, bouncesFilterModel]);

  useEffect(() => {
    if (tabValue === 2) fetchComplaints();
  }, [tabValue, selectedClientId, complaintsDateRange, complaintsPaginationModel, complaintsSortModel, complaintsFilterModel]);

  // Fetch unsubscribes
  const fetchUnsubscribes = async () => {
    if (!selectedClientId) return;
    setUnsubscribesLoading(true);
    setUnsubscribesError(null);
    const { startDate, endDate, days } = getStartEndFromDateRange(unsubscribesDateRange);
    const skip = unsubscribesPaginationModel.page * unsubscribesPaginationModel.pageSize;
    const limit = unsubscribesPaginationModel.pageSize;
    
    // Calculate actual days from date range for by-day API
    let actualDays = days || 30;
    if (unsubscribesDateRange.date_range === 'custom' && unsubscribesDateRange.startDate && unsubscribesDateRange.endDate) {
      const start = new Date(unsubscribesDateRange.startDate);
      const end = new Date(unsubscribesDateRange.endDate);
      actualDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    }
    
    // Get sorting parameters
    const sortBy = unsubscribesSortModel.length > 0 ? mapUnsubscribesFieldName(unsubscribesSortModel[0].field) : undefined;
    const sortOrder = unsubscribesSortModel.length > 0 ? (unsubscribesSortModel[0].sort || undefined) : undefined;
    
    // Get filter parameters
    const filters = mapUnsubscribesFilters(unsubscribesFilterModel);
    
    Promise.all([
      apiService.getUnsubscribes({ 
        clientId: selectedClientId, 
        startDate, 
        endDate, 
        skip, 
        limit, 
        sortBy, 
        sortOrder,
        ...filters
      }) as Promise<{ data: any[], total?: number }>,
      apiService.getUnsubscribesByDay({ clientId: selectedClientId, days: actualDays }) as Promise<{ data: any[] }>
    ])
      .then(([listRes, byDayRes]) => {
        setUnsubscribes(listRes.data as any[]);
        setUnsubscribesByDay(byDayRes.data as any[]);
        
        // Calculate total count from by-day data
        const totalFromByDay = byDayRes.data.reduce((sum: number, day: any) => sum + (day.count || 0), 0);
        setUnsubscribesTotalCount(totalFromByDay);
      })
      .catch(err => setUnsubscribesError(err.message || 'Failed to load unsubscribes'))
      .finally(() => setUnsubscribesLoading(false));
  };

  useEffect(() => {
    if (tabValue === 3) fetchUnsubscribes();
  }, [tabValue, selectedClientId, unsubscribesDateRange, unsubscribesPaginationModel, unsubscribesSortModel, unsubscribesFilterModel]);

  // Pagination handlers
  const handleBouncesPaginationModelChange = (model: GridPaginationModel) => {
    setBouncesPaginationModel(model);
  };
  const handleComplaintsPaginationModelChange = (model: GridPaginationModel) => {
    setComplaintsPaginationModel(model);
  };
  const handleUnsubscribesPaginationModelChange = (model: GridPaginationModel) => {
    setUnsubscribesPaginationModel(model);
  };

  // Sorting handlers
  const handleBouncesSortModelChange = (model: GridSortModel) => {
    setBouncesSortModel(model);
  };
  const handleComplaintsSortModelChange = (model: GridSortModel) => {
    setComplaintsSortModel(model);
  };
  const handleUnsubscribesSortModelChange = (model: GridSortModel) => {
    setUnsubscribesSortModel(model);
  };

  // Filter handlers
  const handleBouncesFilterModelChange = (model: GridFilterModel) => {
    setBouncesFilterModel(model);
  };
  const handleComplaintsFilterModelChange = (model: GridFilterModel) => {
    setComplaintsFilterModel(model);
  };
  const handleUnsubscribesFilterModelChange = (model: GridFilterModel) => {
    setUnsubscribesFilterModel(model);
  };

  const formatNumber = (num: number) => num.toLocaleString();

  // Field mapping functions to convert DataGrid field names to backend field names
  const mapBouncesFieldName = (field: string): string | undefined => {
    const fieldMap: { [key: string]: string } = {
      'bounce_timestamp': 'bounce_timestamp',
      'bounce_recipient_email': 'bounce_recipient_email',
      'bounce_type': 'bounce_type',
      'bounce_sub_type': 'bounce_sub_type',
      'common_subject': 'common_subject',
    };
    return fieldMap[field];
  };

  const mapComplaintsFieldName = (field: string): string | undefined => {
    const fieldMap: { [key: string]: string } = {
      'complaint_timestamp': 'complaint_timestamp',
      'email_to': 'email_to',
      'complaint_feedback_type': 'complaint_feedback_type',
      'complaint_sub_type': 'complaint_sub_type',
      'common_subject': 'common_subject',
    };
    return fieldMap[field];
  };

  const mapUnsubscribesFieldName = (field: string): string | undefined => {
    const fieldMap: { [key: string]: string } = {
      'dnc_email_date': 'unsubscribe_timestamp',
      'email_address': 'email_address',
      'dnc_email_note': 'unsubscribe_reason',
    };
    return fieldMap[field];
  };

  // Filter mapping functions to convert DataGrid filter models to backend filter parameters
  const mapBouncesFilters = (filterModel: GridFilterModel): any => {
    const filters: any = {};
    filterModel.items.forEach(item => {
      if (item.value) {
        switch (item.field) {
          case 'bounce_recipient_email':
            filters.email_address = item.value;
            break;
          case 'bounce_type':
            filters.bounce_type = item.value;
            break;
          case 'bounce_sub_type':
            filters.bounce_sub_type = item.value;
            break;
          case 'common_subject':
            filters.common_subject = item.value;
            break;
        }
      }
    });
    return filters;
  };

  const mapComplaintsFilters = (filterModel: GridFilterModel): any => {
    const filters: any = {};
    filterModel.items.forEach(item => {
      if (item.value) {
        switch (item.field) {
          case 'email_to':
            filters.email_address = item.value;
            break;
          case 'complaint_feedback_type':
            filters.complaint_feedback_type = item.value;
            break;
          case 'complaint_sub_type':
            filters.complaint_sub_type = item.value;
            break;
          case 'common_subject':
            filters.common_subject = item.value;
            break;
        }
      }
    });
    return filters;
  };

  const mapUnsubscribesFilters = (filterModel: GridFilterModel): any => {
    const filters: any = {};
    filterModel.items.forEach(item => {
      if (item.value) {
        switch (item.field) {
          case 'email_address':
            filters.email_address = item.value;
            break;
          case 'first_name':
            filters.first_name = item.value;
            break;
          case 'last_name':
            filters.last_name = item.value;
            break;
          case 'dnc_email_note':
            filters.dnc_email_note = item.value;
            break;
        }
      }
    });
    return filters;
  };

  const handleBounceRowClick = async (bounce: any) => {
    if (!selectedClientId || !bounce.bounce_recipient_email) return;
    try {
      const res = await contactService.getContacts({
        client_id: selectedClientId,
        email_filter: bounce.bounce_recipient_email,
        limit: 1,
        skip: 0
      });
      if (res.contacts && res.contacts.length > 0) {
        handleOpenContactDetailModal(res.contacts[0]);
      } else {
        setSnackbarMessage('Contact cannot be located.');
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage('Contact lookup failed.');
      setSnackbarOpen(true);
    }
  };

  const handleComplaintRowClick = async (complaint: any) => {
    if (!selectedClientId || !complaint.email_to) return;
    try {
      const res = await contactService.getContacts({
        client_id: selectedClientId,
        email_filter: complaint.email_to,
        limit: 1,
        skip: 0
      });
      if (res.contacts && res.contacts.length > 0) {
        handleOpenContactDetailModal(res.contacts[0]);
      } else {
        setSnackbarMessage('Contact cannot be located.');
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage('Contact lookup failed.');
      setSnackbarOpen(true);
    }
  };

  const handleUnsubscribeRowClick = async (unsubscribe: any) => {
    if (!selectedClientId || !unsubscribe.email_address) return;
    try {
      const res = await contactService.getContacts({
        client_id: selectedClientId,
        email_filter: unsubscribe.email_address,
        limit: 1,
        skip: 0
      });
      if (res.contacts && res.contacts.length > 0) {
        handleOpenContactDetailModal(res.contacts[0]);
      } else {
        setSnackbarMessage('Contact cannot be located.');
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage('Contact lookup failed.');
      setSnackbarOpen(true);
    }
  };

  // Show loading state if no client is selected yet
  if (!selectedClientId || !auth.isAuthenticated || auth.isLoading) {
    return (
      <LoadingState 
        message="Loading client data..." 
        variant="page"
        size="medium"
      />
    );
  }

  if (loading && contacts.length === 0) {
    return (
      <PageContainer>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: 'calc(100vh - 200px)',
          width: '100%'
        }}>
          <LoadingState 
            message="Loading contacts..." 
            variant="page"
            size="medium"
          />
        </Box>
      </PageContainer>
    );
  }

  const columns: GridColDef[] = [
    { 
      field: 'dnc_email', 
      headerName: 'DNC', 
      width: 80,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          {params.value === true ? (
            <Block color="error" fontSize="small" />
          ) : null}
        </Box>
      ),
    },
    { 
      field: 'first_name', 
      headerName: 'First Name', 
      flex: 0.8,
      minWidth: 120,
    },
    { 
      field: 'last_name', 
      headerName: 'Last Name', 
      flex: 0.8,
      minWidth: 120,
    },
    {
      field: 'email_address',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        isICRole() ? obfuscateEmail(params.value) : params.value
      ),
    },
    {
      field: 'segments',
      headerName: 'Segments',
      flex: 2,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams) => {
        const segments = [...params.value].sort((a, b) => a.name.localeCompare(b.name));
        const segmentNames = segments.map((s: { id: number; name: string }) => s.name).join(', ');
        
        return (
          <Tooltip title={segmentNames || 'No segments'} arrow placement="top">
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 0.5, 
                alignItems: 'center', 
                height: '100%',
                width: '100%',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  flexWrap: 'nowrap',
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                {segments.map((segment: { id: number; name: string }) => (
            <Chip
              key={segment.id}
              label={segment.name}
              size="small"
              sx={{
                backgroundColor: getSegmentColor(segment.name),
                color: 'white',
                      flexShrink: 0,
                      maxWidth: '150px',
                '& .MuiChip-label': {
                  px: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                },
              }}
            />
          ))}
        </Box>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      width: 100,
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip title="View Contact Details">
            <IconButton
              aria-label="View Contact"
              onClick={() => handleOpenContactDetailModal(params.row)}
              size="small"
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
{!isICRole() && (
            <Tooltip title="Edit Contact">
              <IconButton
                aria-label="Edit Contact"
                onClick={() => handleOpenContactDetailModal(params.row, true)}
                size="small"
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  // Column definitions for bounces
  const bouncesColumns: GridColDef[] = [
    { 
      field: 'bounce_timestamp', 
      headerName: 'Date', 
      width: 120,
      renderCell: (params: GridRenderCellParams) => 
        params.value ? format(parseISO(params.value), 'yyyy-MM-dd') : ''
    },
    {
      field: 'bounce_recipient_email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          component="span"
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            '&:hover': {
              color: 'primary.dark',
            }
          }}
          onClick={() => handleBounceRowClick(params.row)}
        >
          {isICRole() ? obfuscateEmail(params.value) : params.value}
        </Typography>
      ),
    },
    { 
      field: 'bounce_type', 
      headerName: 'Bounce Type', 
      width: 120,
    },
    { 
      field: 'bounce_sub_type', 
      headerName: 'Type', 
      width: 120,
    },
    { 
      field: 'common_subject', 
      headerName: 'Subject', 
      flex: 1,
      minWidth: 200,
    },
  ];

  // Column definitions for complaints
  const complaintsColumns: GridColDef[] = [
    { 
      field: 'complaint_timestamp', 
      headerName: 'Date', 
      width: 120,
      renderCell: (params: GridRenderCellParams) => 
        params.value ? format(parseISO(params.value), 'yyyy-MM-dd') : ''
    },
    {
      field: 'email_to',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          component="span"
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            '&:hover': {
              color: 'primary.dark',
            }
          }}
          onClick={() => handleComplaintRowClick(params.row)}
        >
          {isICRole() ? obfuscateEmail(params.value) : params.value}
        </Typography>
      ),
    },
    { 
      field: 'complaint_feedback_type', 
      headerName: 'Feedback Type', 
      width: 150,
    },
    { 
      field: 'complaint_sub_type', 
      headerName: 'Sub Type', 
      width: 120,
    },
    { 
      field: 'common_subject', 
      headerName: 'Subject', 
      flex: 1,
      minWidth: 200,
    },
  ];

  // Column definitions for unsubscribes
  const unsubscribesColumns: GridColDef[] = [
    { 
      field: 'dnc_email_date', 
      headerName: 'Date', 
      width: 120,
      renderCell: (params: GridRenderCellParams) => 
        params.value ? format(parseISO(params.value), 'yyyy-MM-dd') : ''
    },
    {
      field: 'email_address',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          component="span"
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            '&:hover': {
              color: 'primary.dark',
            }
          }}
          onClick={() => handleUnsubscribeRowClick(params.row)}
        >
          {isICRole() ? obfuscateEmail(params.value) : params.value}
        </Typography>
      ),
    },
    { 
      field: 'first_name', 
      headerName: 'First Name', 
      flex: 0.8,
      minWidth: 120,
    },
    { 
      field: 'last_name', 
      headerName: 'Last Name', 
      flex: 0.8,
      minWidth: 120,
    },
    { 
      field: 'dnc_email_note', 
      headerName: 'Note', 
      flex: 1,
      minWidth: 200,
    },
  ];

  return (
    <PageContainer>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Contacts
      </Typography>
      <Paper elevation={2} className="p-6">
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          className="bg-white"
        >
          <Tab icon={<ListAlt />} iconPosition="start" label="List" {...a11yProps(0)} />
          <Tab icon={<Reply />} iconPosition="start" label="Bounces" {...a11yProps(1)} />
          <Tab icon={<Feedback />} iconPosition="start" label="Complaints" {...a11yProps(2)} />
          <Tab icon={<Unsubscribe />} iconPosition="start" label="Unsubscribes" {...a11yProps(3)} />
        </Tabs>
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2, mx: -3 }}>
            <Grid container spacing={2} alignItems="flex-start" sx={{ px: 3 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by email or name..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{ height: 40 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch}>
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={!!searchError}
                  helperText={searchError}
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl variant="outlined" size="medium" sx={{ flex: 1, minWidth: 250 }}>
                    <InputLabel id="segment-filter-label">Filter by Segment</InputLabel>
                    <Select
                      labelId="segment-filter-label"
                      id="segment-filter"
                      value={selectedSegment}
                      onChange={handleSegmentChange}
                      label="Filter by Segment"
                      disabled={segmentsLoading}
                      endAdornment={
                        selectedSegment && (
                          <InputAdornment position="end" sx={{ mr: 1 }}>
                            <IconButton size="small" onClick={handleClearFilters}>
                              <Clear />
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                      sx={{ 
                        height: 42,
                        '.MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          paddingTop: 0,
                          paddingBottom: 0,
                          padding: '0 32px 0 14px',
                          lineHeight: '42px'
                        },
                        '.MuiInputLabel-root': {
                          display: 'flex',
                          alignItems: 'center',
                          transform: 'translate(14px, 4px) scale(1)'
                        },
                        '.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.75)'
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>All Segments</em>
                      </MenuItem>
                      {segments
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((segment) => (
                        <MenuItem key={segment.id} value={segment.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box 
                              sx={{ 
                                width: 12, 
                                height: 12, 
                                borderRadius: '50%', 
                                backgroundColor: getSegmentColor(segment.name),
                                mr: 1
                              }} 
                            />
                            {segment.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!isICRole() && (
                      <Tooltip title="Add New Contact">
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleOpenAddContactModal}
                          size="small"
                          sx={{
                            height: 42,
                            px: 2,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Add Contact
                        </Button>
                      </Tooltip>
                    )}
                    {!isICRole() && (
                      <Tooltip title="Export contacts to CSV">
                        <span>
                          <IconButton
                            onClick={handleDirectExport}
                            disabled={!selectedClientId || loading || isExporting}
                            color="primary"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              position: 'relative',
                              '.MuiCircularProgress-root': {
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)'
                              }
                            }}
                          >
                            {isExporting ? (
                              <>
                                <CircularProgress size={24} />
                                <Typography variant="body2" sx={{ marginLeft: 0.5 }}>
                                  {exportProgress}%
                                </Typography>
                              </>
                            ) : (
                              <>
                                <DownloadIcon />
                                <Typography variant="body2" sx={{ marginLeft: 0.5 }}>
                                  Export CSV
                                </Typography>
                              </>
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Paper sx={{ height: 'calc(100vh - 300px)', width: '100%' }}>
            {contacts.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>No contacts found</Typography>
              </Box>
            ) : (
              <DataGrid
                rows={contacts.map(contact => {
                  // Ensure we have valid data for each field
                  const safeContact = {
                    id: contact.id,
                    first_name: contact.first_name || '',
                    last_name: contact.last_name || '',
                    email_address: contact.email_address || '',
                    dnc_email: Boolean(contact.dnc_email),
                    segments: contact.segments || []
                  };
                  return safeContact;
                })}
                columns={columns}
                rowCount={totalCount}
                paginationMode="server"
                sortingMode="server"
                onPaginationModelChange={handlePaginationModelChange}
                onSortModelChange={handleSortModelChange}
                sortModel={sortModel}
                filterModel={filterModel}
                onFilterModelChange={setFilterModel}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 100 },
                  },
                  sorting: {
                    sortModel: [{ field: 'email_address', sort: 'asc' }],
                  },
                  density: 'compact',
                }}
                pageSizeOptions={[25, 100, 300]}
                disableRowSelectionOnClick
                loading={loading}
                getRowId={(row) => row.id}
                autoHeight={false}
                disableColumnMenu={false}
                disableColumnFilter={true}
                disableColumnSelector={false}
                disableDensitySelector={false}
                sx={{
                  '& .MuiDataGrid-cell': {
                    padding: '0 16px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiDataGrid-columnHeader': {
                    padding: '0 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              />
            )}
          </Paper>
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Reply color="primary" />
              <Typography variant="h4" fontWeight={700}>
                {formatNumber(selectedBounceDate ? bounces.filter((bounce: any) => bounce.bounce_timestamp && bounce.bounce_timestamp.startsWith(selectedBounceDate)).length : bouncesTotalCount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Bounces
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DateRangePicker
                dateRange={bouncesDateRange}
                onDateRangeChange={setBouncesDateRange}
              />
              {!isICRole() && (
                <Tooltip title="Export bounces to CSV">
                  <span>
                    <IconButton
                      onClick={handleBouncesExport}
                      disabled={!selectedClientId || bouncesLoading || isExporting}
                      color="primary"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        position: 'relative',
                        '.MuiCircularProgress-root': {
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)'
                        }
                      }}
                    >
                      {isExporting ? (
                        <>
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ marginLeft: 0.5 }}>
                            {exportProgress}%
                          </Typography>
                        </>
                      ) : (
                        <>
                          <DownloadIcon />
                          <Typography variant="body2" sx={{ marginLeft: 0.5 }}>
                            Export CSV
                          </Typography>
                        </>
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box sx={{ height: 300, my: 3 }}>
            {bouncesLoading ? <CircularProgress /> : bouncesError ? <Alert severity="error">{bouncesError}</Alert> : (
              bouncesByDay && bouncesByDay.length > 0 ? (
                <ResponsiveLine
                  data={[{
                    id: 'bounces',
                    color: 'hsl(0, 70%, 50%)',
                    data: bouncesByDay
                      .filter((d: any) => d && d.date && d.count !== undefined)
                      .map((d: any) => ({ x: d.date.split('T')[0], y: d.count }))
                  }]}
                  margin={{ top: 30, right: 40, bottom: 70, left: 70 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                  curve="monotoneX"
                  axisBottom={{ tickSize: 5, tickPadding: 12, tickRotation: -45, legend: 'Date', legendOffset: 55, legendPosition: 'middle' }}
                  axisLeft={{ tickSize: 5, tickPadding: 8, tickRotation: 0, legend: 'Bounces', legendOffset: -50, legendPosition: 'middle' }}
                  colors={{ scheme: 'category10' }}
                  pointSize={10}
                  pointColor={{ theme: 'background' }}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  pointLabelYOffset={-12}
                  useMesh={true}
                  onClick={point => {
                    const clickedDate = (point as any).data.x as string;
                    setSelectedBounceDate(prev => prev === clickedDate ? null : clickedDate);
                  }}
                  tooltip={({ point }) => (
                    <div style={{ padding: '6px 12px', background: 'white', border: '1px solid #ccc', borderRadius: 4 }}>
                      <div><strong>{String(point.data.x)}</strong></div>
                      <div>Bounces: {String(point.data.y)}</div>
                    </div>
                  )}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No bounce data available</Typography>
                </Box>
              )
            )}
          </Box>
          <Paper sx={{ height: 'calc(100vh - 500px)', width: '100%' }}>
            {bounces.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>No bounces found</Typography>
              </Box>
            ) : (
              <DataGrid
                rows={bounces.map(bounce => ({
                  id: bounce.id,
                  bounce_timestamp: bounce.bounce_timestamp,
                  bounce_recipient_email: bounce.bounce_recipient_email,
                  bounce_type: bounce.bounce_type,
                  bounce_sub_type: bounce.bounce_sub_type,
                  common_subject: bounce.common_subject,
                }))}
                columns={bouncesColumns}
                rowCount={bouncesTotalCount}
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                onPaginationModelChange={handleBouncesPaginationModelChange}
                onSortModelChange={handleBouncesSortModelChange}
                onFilterModelChange={handleBouncesFilterModelChange}
                sortModel={bouncesSortModel}
                filterModel={bouncesFilterModel}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 25 },
                  },
                  sorting: {
                    sortModel: [{ field: 'bounce_timestamp', sort: 'desc' }],
                  },
                  density: 'compact',
                }}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                loading={bouncesLoading}
                getRowId={(row) => row.id}
                autoHeight={false}
                disableColumnMenu={false}
                disableColumnFilter={false}
                disableColumnSelector={false}
                disableDensitySelector={false}
                sx={{
                  '& .MuiDataGrid-cell': {
                    padding: '0 16px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiDataGrid-columnHeader': {
                    padding: '0 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              />
            )}
          </Paper>
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Feedback color="primary" />
              <Typography variant="h4" fontWeight={700}>
                {formatNumber(selectedComplaintDate ? complaints.filter((complaint: any) => complaint.complaint_timestamp && complaint.complaint_timestamp.startsWith(selectedComplaintDate)).length : complaintsTotalCount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Complaints
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DateRangePicker
                dateRange={complaintsDateRange}
                onDateRangeChange={setComplaintsDateRange}
              />
              {!isICRole() && (
                <Tooltip title="Export complaints to CSV">
                  <span>
                    <IconButton
                      onClick={handleComplaintsExport}
                      disabled={!selectedClientId || complaintsLoading || isExporting}
                      color="primary"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        position: 'relative',
                        '.MuiCircularProgress-root': {
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)'
                        }
                      }}
                    >
                      {isExporting ? (
                        <>
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ marginLeft: 0.5 }}>
                            {exportProgress}%
                          </Typography>
                        </>
                      ) : (
                        <>
                          <DownloadIcon />
                          <Typography variant="body2" sx={{ marginLeft: 0.5 }}>
                            Export CSV
                          </Typography>
                        </>
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box sx={{ height: 300, my: 3 }}>
            {complaintsLoading ? <CircularProgress /> : complaintsError ? <Alert severity="error">{complaintsError}</Alert> : (
              complaintsByDay && complaintsByDay.length > 0 ? (
                <ResponsiveLine
                  data={[{
                    id: 'complaints',
                    color: 'hsl(30, 70%, 50%)',
                    data: complaintsByDay
                      .filter((d: any) => d && d.date && d.count !== undefined)
                      .map((d: any) => ({ x: d.date.split('T')[0], y: d.count }))
                  }]}
                  margin={{ top: 30, right: 40, bottom: 70, left: 70 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                  curve="monotoneX"
                  axisBottom={{ tickSize: 5, tickPadding: 12, tickRotation: -45, legend: 'Date', legendOffset: 55, legendPosition: 'middle' }}
                  axisLeft={{ tickSize: 5, tickPadding: 8, tickRotation: 0, legend: 'Complaints', legendOffset: -50, legendPosition: 'middle' }}
                  colors={{ scheme: 'category10' }}
                  pointSize={10}
                  pointColor={{ theme: 'background' }}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  pointLabelYOffset={-12}
                  useMesh={true}
                  onClick={point => {
                    const clickedDate = (point as any).data.x as string;
                    setSelectedComplaintDate(prev => prev === clickedDate ? null : clickedDate);
                  }}
                  tooltip={({ point }) => (
                    <div style={{ padding: '6px 12px', background: 'white', border: '1px solid #ccc', borderRadius: 4 }}>
                      <div><strong>{String(point.data.x)}</strong></div>
                      <div>Complaints: {String(point.data.y)}</div>
                    </div>
                  )}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No complaint data available</Typography>
                </Box>
              )
            )}
          </Box>
          <Paper sx={{ height: 'calc(100vh - 500px)', width: '100%' }}>
            {complaints.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>No complaints found</Typography>
              </Box>
            ) : (
              <DataGrid
                rows={complaints.map(complaint => ({
                  id: complaint.id,
                  complaint_timestamp: complaint.complaint_timestamp,
                  email_to: complaint.email_to,
                  complaint_feedback_type: complaint.complaint_feedback_type,
                  complaint_sub_type: complaint.complaint_sub_type,
                  common_subject: complaint.common_subject,
                }))}
                columns={complaintsColumns}
                rowCount={complaintsTotalCount}
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                onPaginationModelChange={handleComplaintsPaginationModelChange}
                onSortModelChange={handleComplaintsSortModelChange}
                onFilterModelChange={handleComplaintsFilterModelChange}
                sortModel={complaintsSortModel}
                filterModel={complaintsFilterModel}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 25 },
                  },
                  sorting: {
                    sortModel: [{ field: 'complaint_timestamp', sort: 'desc' }],
                  },
                  density: 'compact',
                }}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                loading={complaintsLoading}
                getRowId={(row) => row.id}
                autoHeight={false}
                disableColumnMenu={false}
                disableColumnFilter={false}
                disableColumnSelector={false}
                disableDensitySelector={false}
                sx={{
                  '& .MuiDataGrid-cell': {
                    padding: '0 16px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiDataGrid-columnHeader': {
                    padding: '0 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              />
            )}
          </Paper>
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Unsubscribe color="primary" />
              <Typography variant="h4" fontWeight={700}>
                {formatNumber(selectedUnsubscribeDate ? unsubscribes.filter((unsubscribe: any) => unsubscribe.dnc_email_date && unsubscribe.dnc_email_date.startsWith(selectedUnsubscribeDate)).length : unsubscribesTotalCount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Unsubscribes
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DateRangePicker
                dateRange={unsubscribesDateRange}
                onDateRangeChange={setUnsubscribesDateRange}
              />
              {!isICRole() && (
                <Tooltip title="Export unsubscribes to CSV">
                  <span>
                    <IconButton
                      onClick={handleUnsubscribesExport}
                      disabled={!selectedClientId || unsubscribesLoading || isExporting}
                      color="primary"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        position: 'relative',
                        '.MuiCircularProgress-root': {
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)'
                        }
                      }}
                    >
                      {isExporting ? (
                        <>
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ marginLeft: 0.5 }}>
                            {exportProgress}%
                          </Typography>
                        </>
                      ) : (
                        <>
                          <DownloadIcon />
                          <Typography variant="body2" sx={{ marginLeft: 0.5 }}>
                            Export CSV
                          </Typography>
                        </>
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box sx={{ height: 300, my: 3 }}>
            {unsubscribesLoading ? <CircularProgress /> : unsubscribesError ? <Alert severity="error">{unsubscribesError}</Alert> : (
              unsubscribesByDay && unsubscribesByDay.length > 0 ? (
                <ResponsiveLine
                  data={[{
                    id: 'unsubscribes',
                    color: 'hsl(120, 70%, 50%)',
                    data: unsubscribesByDay
                      .filter((d: any) => d && d.date && d.count !== undefined)
                      .map((d: any) => ({ 
                        x: d.date.split('T')[0], 
                        y: d.count 
                      }))
                  }]}
                  margin={{ top: 30, right: 40, bottom: 70, left: 70 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                  curve="monotoneX"
                  axisBottom={{ tickSize: 5, tickPadding: 12, tickRotation: -45, legend: 'Date', legendOffset: 55, legendPosition: 'middle' }}
                  axisLeft={{ tickSize: 5, tickPadding: 8, tickRotation: 0, legend: 'Unsubscribes', legendOffset: -50, legendPosition: 'middle' }}
                  colors={{ scheme: 'category10' }}
                  pointSize={10}
                  pointColor={{ theme: 'background' }}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  pointLabelYOffset={-12}
                  useMesh={true}
                  onClick={point => {
                    const clickedDate = (point as any).data.x as string;
                    setSelectedUnsubscribeDate(prev => prev === clickedDate ? null : clickedDate);
                  }}
                  tooltip={({ point }) => (
                    <div style={{ padding: '6px 12px', background: 'white', border: '1px solid #ccc', borderRadius: 4 }}>
                      <div><strong>{String(point.data.x)}</strong></div>
                      <div>Unsubscribes: {String(point.data.y)}</div>
                    </div>
                  )}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No unsubscribe data available</Typography>
                </Box>
              )
            )}
          </Box>
          <Paper sx={{ height: 'calc(100vh - 500px)', width: '100%' }}>
            {unsubscribes.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>No unsubscribes found</Typography>
              </Box>
            ) : (
              <DataGrid
                rows={unsubscribes.map(unsubscribe => ({
                  id: unsubscribe.id,
                  dnc_email_date: unsubscribe.dnc_email_date,
                  email_address: unsubscribe.email_address,
                  first_name: unsubscribe.first_name,
                  last_name: unsubscribe.last_name,
                  dnc_email_note: unsubscribe.dnc_email_note,
                }))}
                columns={unsubscribesColumns}
                rowCount={unsubscribesTotalCount}
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                onPaginationModelChange={handleUnsubscribesPaginationModelChange}
                onSortModelChange={handleUnsubscribesSortModelChange}
                onFilterModelChange={handleUnsubscribesFilterModelChange}
                sortModel={unsubscribesSortModel}
                filterModel={unsubscribesFilterModel}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 25 },
                  },
                  sorting: {
                    sortModel: [{ field: 'dnc_email_date', sort: 'desc' }],
                  },
                  density: 'compact',
                }}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                loading={unsubscribesLoading}
                getRowId={(row) => row.id}
                autoHeight={false}
                disableColumnMenu={false}
                disableColumnFilter={false}
                disableColumnSelector={false}
                disableDensitySelector={false}
                sx={{
                  '& .MuiDataGrid-cell': {
                    padding: '0 16px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiDataGrid-columnHeader': {
                    padding: '0 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              />
            )}
          </Paper>
        </TabPanel>
      </Paper>

      <Snackbar
        open={!!exportSuccess}
        autoHideDuration={5000}
        onClose={() => setExportSuccess(null)}
        message={exportSuccess}
      />
      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>

      {/* Add Contact Modal */}
      <ContactModal
        key={`add-contact-modal-${selectedClientId}`}
        open={isAddContactModalOpen}
        onClose={handleCloseAddContactModal}
        onContactCreated={handleContactCreated}
      />

      {/* Contact Detail Modal */}
      {selectedContactForDetail && (
        <ContactDetailModal
          key={`contact-detail-modal-${selectedContactForDetail.client_id}-${selectedContactForDetail.id}`}
          open={isContactDetailModalOpen}
          onClose={handleCloseContactDetailModal}
          contactId={selectedContactForDetail.id}
          clientId={selectedContactForDetail.client_id}
          initialEditMode={openInEditMode}
        />
      )}
    </PageContainer>
  );
};

export default Contacts; 
